import {
  Button,
  ButtonColor,
  ButtonSize,
  FileUpload,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { ModelError, UploadDocumentResponse } from 'src/front/api/generated';
import previewIcon from 'src/front/assets/icon_preview.svg';
import { ACCEPTED_FILE_UPLOAD_FORMATS } from 'src/front/common/constants/files';
import { isInvalidFileError } from 'src/front/common/utils/errorUtils';
import { checkAcceptedFileType, openDocument } from 'src/front/common/utils/fileUploadUtils';

import { formatFreeText } from 'src/front/common/utils/formUtils';
import { SimpleDocumentUploadSchema } from 'src/front/common/utils/validationSchemas';

import { ButtonsRow, FormElement, FormRow, Label } from 'src/front/components/FormStyles';
import styled from 'styled-components';

import AsyncButton from './AsyncButton';

export const StyledFormRow = styled(FormRow)`
  width: 700px;
  & > div {
    width: 500px;
  }
`;
export const StyledPreviewButton = styled.button`
  position: relative;

  border: none;
  margin-right: 20px;

  background: none;

  &&:focus {
    outline: none;
  }
`;
export interface UploadDocumentFormData {
  files?: FileList | null;
  filePath?: string;
  explanation: string;
}

const defaultValues = {
  files: undefined,
  filePath: '',
  explanation: '',
};

interface Props {
  submit: (data: UploadDocumentFormData) => void;
  error?: ModelError;
  defaultExplanation?: string;
  uploadedDocument?: UploadDocumentResponse;
  setUploadedDocument: Dispatch<SetStateAction<UploadDocumentResponse | undefined>>;
  handleContinue: () => Promise<void>;
  hideContinueButton?: boolean;
  disableUploadButton?: boolean;
}

const UploadDocumentForm: FC<Props> = ({
  submit,
  uploadedDocument,
  setUploadedDocument,
  defaultExplanation,
  error,
  handleContinue,
  hideContinueButton = false,
  disableUploadButton = false,
}) => {
  const { t } = useTranslation();
  const [previewAvailable, setPreviewAvailable] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const {
    watch,
    register,
    errors,
    setValue,
    handleSubmit,
    control,
    formState,
    setError,
    getValues,
  } = useForm<UploadDocumentFormData>({
    defaultValues,
    resolver: yupResolver(SimpleDocumentUploadSchema),
  });

  const registerFields = useCallback(() => {
    register({ name: 'files' });
    register({ name: 'filePath' });
  }, [register]);

  useEffect(() => {
    if (error && isInvalidFileError(error)) {
      setError('files', { type: 'required', message: 'general_uploadFileFormat_errorMessage' });
    }
  }, [error, setError]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  const handleFileSelect = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setValue('files', target.files, { shouldValidate: true, shouldDirty: true });
    setValue('filePath', target.value);
    setUploadedDocument(undefined);
    setPreviewConfiguration(target.files);
    setIsSubmitted(false);
  };
  const onPreview = (): void => {
    const { files } = getValues();
    openDocument(files);
  };

  const setPreviewConfiguration = async (data?: FileList | null): Promise<void> => {
    const acceptStatus = await checkAcceptedFileType(data);
    setPreviewAvailable(acceptStatus);
    acceptStatus && onPreview();
  };
  const onSubmit = (data: UploadDocumentFormData): void => {
    setIsSubmitted(true);
    submit(data);
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Label>
        <Trans i18nKey="cardEntry_documentEntry_uploadDocument_text" />
      </Label>
      <FormRow>
        <FormElement>
          <FileUpload
            accept={ACCEPTED_FILE_UPLOAD_FORMATS}
            description={t('general_uploadFile_text')}
            errorMessage={t(errors?.files?.message as string)}
            label={t('general_uploadDocument_label')}
            name="file"
            onChange={handleFileSelect}
            value={watch('filePath')}
            required
          >
            {previewAvailable ? (
              <StyledPreviewButton onClick={onPreview} type="button">
                <img alt="previewIcon" src={previewIcon} />
              </StyledPreviewButton>
            ) : null}
          </FileUpload>
        </FormElement>
      </FormRow>
      <StyledFormRow>
        <Controller
          control={control}
          name="explanation"
          render={({ onChange, value }) => (
            <TextInput
              disabled={Boolean(defaultExplanation)}
              errorMessage={t(errors?.explanation?.message as string)}
              formatter={formatFreeText}
              label={t('general_note_label')}
              maxLength={50}
              name="explanation"
              onChange={onChange}
              value={defaultExplanation || value}
            />
          )}
        />
      </StyledFormRow>
      <ButtonsRow>
        <Button
          color={ButtonColor.Secondary}
          disabled={
            !!uploadedDocument || isSubmitted || formState.isSubmitting || disableUploadButton
          }
          size={ButtonSize.Medium}
          text={t('general_uploadDocument_label')}
          type="submit"
        />
        {hideContinueButton ? null : (
          <AsyncButton
            color={ButtonColor.Primary}
            disabled={!uploadedDocument}
            onClick={handleContinue}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="button"
          />
        )}
      </ButtonsRow>
    </form>
  );
};

export default UploadDocumentForm;
